import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { concatMap, distinctUntilChanged, map, tap } from 'rxjs/operators';
import {
  EvaluationContext,
  OpenFeature,
  ProviderEvents,
  ProviderStatus,
} from '@openfeature/angular-sdk';
import { AuthenticateUserState } from '../store/authenticate-user.state';
import { AppliedFlagContext, COMPANY_FLAG_DOMAIN } from './applied-flag-context';

/**
 * Keeps the OpenFeature evaluation context in step with the current SYRC primary company, so a
 * ConfigCat targeting rule can turn a flag on for one pilot company (SYRC-7533).
 *
 * The context mirrors the lambdas' FeatureFlagService (SYRC-7532): the raw primaryCompanyId is the
 * ConfigCat Identifier (targetingKey) and also a `primaryCompanyId` custom attribute, so one rule
 * covers the web app and the backend. With no company the context is empty and flags are evaluated
 * for the environment as a whole, as on the backend.
 *
 * The context is set on COMPANY_FLAG_DOMAIN, not globally, so only the flags read through that
 * domain (the hierarchy phase 1 and lazy picker flags) are evaluated with it. Every other flag keeps the default
 * domain's empty context and evaluates exactly as it did before SYRC-7533.
 *
 * Started eagerly from the AppModule constructor.
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagContextService {
  private readonly store = inject(Store);
  private readonly appliedContext = inject(AppliedFlagContext);
  private readonly destroyRef = inject(DestroyRef);
  // The company most recently seen in the store; only its context is marked applied.
  private latestCompanyId: string | null = null;

  constructor() {
    this.watchCompany();
  }

  private watchCompany(): void {
    this.store
      .select(AuthenticateUserState.getSyrcPrimaryCompanyId)
      .pipe(
        map((companyId) => companyId || ''),
        distinctUntilChanged(),
        // Synchronously, in the same dispatch as the switch: readers wait instead of getting the
        // previous company's context, which is still applied.
        tap((companyId) => {
          this.latestCompanyId = companyId;
          this.appliedContext.markPending();
        }),
        // In order, so a quick double switch can't leave the older company's context in place.
        concatMap((companyId) => this.apply(companyId)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private async apply(companyId: string): Promise<void> {
    try {
      // A company is only marked applied once ConfigCat has answered (or failed to), so the first
      // phase 1 value is ConfigCat's, not the default. Setting a context on an unbound domain runs no
      // provider handler, so it can't change the provider's status for other readers.
      // Don't add a timeout here: ConfigCat's own init timeout bounds the wait.
      await providerSettled();
      const context: EvaluationContext = companyId
        ? { targetingKey: companyId, primaryCompanyId: companyId }
        : {};
      if (!sameCompany(OpenFeature.getContext(COMPANY_FLAG_DOMAIN), context)) {
        await OpenFeature.setContext(COMPANY_FLAG_DOMAIN, context);
      }
    } catch (error) {
      console.error('Feature flag context could not be set', error);
    } finally {
      // Always mark it, even on failure, so per-company readers can't wait forever. Skip it when a
      // newer company is already queued: that one is marked when it has been applied.
      if (companyId === this.latestCompanyId) {
        this.appliedContext.markApplied(companyId);
      }
    }
  }
}

/** Resolves once the provider has left NOT_READY: initialized, or failed to initialize. */
function providerSettled(): Promise<void> {
  const client = OpenFeature.getClient();
  if (client.providerStatus !== ProviderStatus.NOT_READY) {
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    const controller = new AbortController();
    const done = () => {
      controller.abort();
      resolve();
    };
    client.addHandler(ProviderEvents.Ready, done, { signal: controller.signal });
    client.addHandler(ProviderEvents.Error, done, { signal: controller.signal });
  });
}

function sameCompany(current: EvaluationContext, next: EvaluationContext): boolean {
  return (
    Object.keys(current).length === Object.keys(next).length &&
    current.targetingKey === next.targetingKey &&
    current['primaryCompanyId'] === next['primaryCompanyId']
  );
}
